// ** react imports
import React, { useEffect, useState, useMemo } from 'react';

// ** toast imports
import { toast } from 'react-toastify';

// ** custom hooks imports
import { useGetAllUsers, useDeleteUser } from '../../../hooks/apis/useUsers';

// ** views imports
import CustomersTable from '../../../views/customers/CustomerList/CustomersTable';
import AccountSelect from '../../../views/customers/CustomerList/AccountSelect';
import CustomerFilterButton from '../../../views/customers/CustomerList/CustomerFilterButton';
import CustomerPagination from '../../../views/customers/CustomerList/CustomerPagination';
import DeleteCustomerModal from '../../../views/customers/CustomerList/DeleteCustomerModal';

// ** components imports
import DeleteButton from '../../../partials/actions/DeleteButton';

// ** enums imports
import { USER_ROLES, ACCOUNT_TYPES } from '../../../enums/common';

function CustomerList() {
  // states
  const [selectedItems, setSelectedItems] = useState([]);
  const [query, setQuery] = useState({
    page: 1,
    limit: 10,
    roles: USER_ROLES.CUSTOMER,
    accountType: null,
    sortBy: 'userId',
    sortOrder: 'asc',
    isActive: true, // Only show active customers by default
    search: '',
    isEmailVerified: undefined,
    isPasswordSet: undefined,
  });

  // custom hooks
  const { data, isGetAllUsersLoading, isGetAllUsersFetching, getAllUsers } =
    useGetAllUsers();
  
  const { deleteUser, isDeleteUserLoading } = useDeleteUser();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);

  // fetch customers whenever query changes
  useEffect(() => {
    // Filter out null/undefined values from query before sending to API
    const cleanQuery = Object.fromEntries(
      Object.entries(query).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    );
    getAllUsers(cleanQuery);
  }, [query, getAllUsers]);

  // normalize customers data for table and pagination
  const tableData = useMemo(() => {
    return {
      data: data?.data || [],
      total: data?.total || 0,
      currentPage: query.page,
      totalPages: Math.ceil((data?.total || 0) / query.limit),
    };
  }, [data, query.page, query.limit]);

  // handlers
  const handleSelectedItems = selectedItems => {
    setSelectedItems([...selectedItems]);
  };

  const handleAccountTypeChange = accountType => {
    setQuery(prev => ({
      ...prev,
      accountType,
      page: 1, // Reset to first page when changing filters
    }));
  };

  const handleStatusFilter = statusFilters => {
    setQuery(prev => ({
      ...prev,
      ...statusFilters,
      page: 1, // Reset to first page when changing filters
    }));
  };

  const handleSearchFilter = searchTerm => {
    setQuery(prev => ({
      ...prev,
      search: searchTerm,
      page: 1, // Reset to first page when changing filters
    }));
  };

  const handleSortChange = (sortBy, sortOrder) => {
    setQuery(prev => ({
      ...prev,
      sortBy,
      sortOrder,
    }));
  };

  const handlePageChange = page => {
    setQuery(prev => ({
      ...prev,
      page,
    }));
  };

  const handleLimitChange = limit => {
    setQuery(prev => ({
      ...prev,
      limit,
      page: 1, // Reset to first page when changing limit
    }));
  };

  const handleResetFilters = () => {
    setQuery({
      page: 1,
      limit: 10,
      roles: USER_ROLES.CUSTOMER,
      accountType: ACCOUNT_TYPES.REGISTER,
      sortBy: 'phone',
      sortOrder: 'desc',
      isActive: true,
      search: '',
      isEmailVerified: undefined,
      isPasswordSet: undefined,
    });
  };

  const handleDeleteClick = (customer) => {
    setCustomerToDelete(customer);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!customerToDelete) return;

    const response = await deleteUser(customerToDelete._id);
    if (response?.data) {
      setDeleteModalOpen(false);
      setCustomerToDelete(null);
      const cleanQuery = Object.fromEntries(
          Object.entries(query).filter(
            ([_, value]) => value !== null && value !== undefined && value !== ''
          )
        );
      getAllUsers(cleanQuery);
    } else {
      toast.error(response?.error?.data?.message || 'Failed to delete customer');
    }
  };

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            {/* Left: Title */}
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Customers
              </h1>
            </div>

            {/* Right: Actions */}
            <div className="grid grid-flow-col sm:auto-cols-max justify-start sm:justify-end gap-2">
              {/* Delete button */}
              <DeleteButton selectedItems={selectedItems} />

              {/* Account Type Filter */}
              <AccountSelect
                onChange={handleAccountTypeChange}
                currentValue={query.accountType}
              />

              {/* Advanced Filter button */}
              <CustomerFilterButton
                align="right"
                onStatusFilter={handleStatusFilter}
                onSearchFilter={handleSearchFilter}
                onReset={handleResetFilters}
                currentFilters={useMemo(
                  () => ({
                    isActive: query.isActive,
                    isEmailVerified: query.isEmailVerified,
                    isPasswordSet: query.isPasswordSet,
                    search: query.search,
                  }),
                  [
                    query.isActive,
                    query.isEmailVerified,
                    query.isPasswordSet,
                    query.search,
                  ]
                )}
              />
            </div>
          </div>

          {/* Active Filters Display */}
          {(query.search ||
            query.isEmailVerified !== undefined ||
            query.isPasswordSet !== undefined ||
            query.isActive !== true) && (
            <div className="mb-4 p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-700 transition-all duration-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-start sm:items-center gap-2 text-sm text-violet-700 dark:text-violet-300">
                  <svg
                    className="w-4 h-4 mt-0.5 sm:mt-0 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                    />
                  </svg>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <span className="font-medium whitespace-nowrap">
                      Active Filters:
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      {query.search && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Search: &quot;
                          {query.search.length > 20
                            ? query.search.substring(0, 20) + '...'
                            : query.search}
                          &quot;
                        </span>
                      )}
                      {query.isEmailVerified !== undefined && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Email Verified: {query.isEmailVerified ? 'Yes' : 'No'}
                        </span>
                      )}
                      {query.isPasswordSet !== undefined && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Password Set: {query.isPasswordSet ? 'Yes' : 'No'}
                        </span>
                      )}
                      {query.isActive !== true && (
                        <span className="px-2 py-1 bg-violet-100 dark:bg-violet-800 text-violet-700 dark:text-violet-300 rounded text-xs font-medium">
                          Status: {query.isActive ? 'Active' : 'Inactive'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleResetFilters}
                  className="text-violet-600 hover:text-violet-700 dark:text-violet-400 dark:hover:text-violet-300 text-sm font-medium whitespace-nowrap transition-colors self-start sm:self-center"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <CustomersTable
            data={tableData}
            selectedItems={handleSelectedItems}
            isGetAllUsersFetching={isGetAllUsersFetching}
            isGetAllUsersLoading={isGetAllUsersLoading}
            onSortChange={handleSortChange}
            currentSortBy={query.sortBy}
            currentSortOrder={query.sortOrder}
            onDeleteClick={handleDeleteClick}
          />

          {/* Delete Confirmation Modal */}
          <DeleteCustomerModal
            modalOpen={deleteModalOpen}
            setModalOpen={setDeleteModalOpen}
            customerData={customerToDelete}
            onConfirm={handleConfirmDelete}
            isLoading={isDeleteUserLoading}
          />

          {/* Pagination */}
          <div className="mt-6">
            <CustomerPagination
              currentPage={tableData.currentPage}
              totalPages={tableData.totalPages}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              currentLimit={query.limit}
              totalItems={tableData.total}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

export default CustomerList;
